import { useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import AppBar from '../../components/AppBar';
import { GlobalInput } from '../../components/GlobalInput';
import { GlobalButton } from '../../components/GlobalButton';
import styles from './page.module.css';
import { Toast } from '../../components/Toast';
import { Link } from 'react-router-dom';
import AppFooter from '../../components/AppFooter';
import ContentGrid from '../../components/ContentGrid';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { getAuthErrorMessage } from '../../utils/authErrors';

export default function ResetPasswordPage() {
  const { t } = useLocale();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(undefined);
    setSuccess(false);

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(true);
    } catch (err) {
      const message = getAuthErrorMessage(err, t, 'reset_password');
      setError(message ?? t('reset_password_unexpected_error'));
    } finally {
      setShowToast(true);
      setIsLoading(false);
    }
  };

  return (
    <div>

      {/* App bar */}
      <AppBar />

      {/* Content */}
      <ContentGrid rows="auto 316px auto 48px">

        {/* Spacer */}
        <div style={{ gridRow: 1 }} />

        {/* Form container */}
        <div className={styles.formContainer}>

          {/* Headline & paragraph */}
          <h1 className={styles.title}>{t('reset_password_title')}</h1>
          <p className={styles.subtitle}>{t('reset_password_subtitle')}</p>
          <form onSubmit={handleReset} className={styles.form}>
            <GlobalInput
              label={t('reset_password_email_label')}
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              required
              error={error}
            />
            <div className={styles.buttonContainer}>
              <GlobalButton
                type="submit"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {t('reset_password_send_button')}
              </GlobalButton>
            </div>
            <p className={styles.signupText}>
              {t('reset_password_remembered_password')}{' '}
              <Link to="/" className={styles.link}>
                {t('reset_password_back_to_login')}
              </Link>
            </p>
          </form>

          <Toast
            message={error ? error : t('reset_password_success_message')}
            type={error ? 'error' : 'success'}
            isVisible={showToast}
            onClose={() => setShowToast(false)}
          />
        </div>

        {/* Spacer */}
        <div style={{ gridRow: 3 }} />

        {/* Footer */}
        <AppFooter inline />
      </ContentGrid>
    </div>
  );
}